import { Slot } from 'primitives/slot'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { useIsInViewport } from 'hooks/use-is-in-viewport'

// --- Types ---

export interface ShowcaseVideo {
  id: string
  previewUrl: string
}

// --- Utilities ---

const DEFAULT_FILENAMES = [
  '1.webm',
  '2.webm',
  '3.webm',
  '4.webm',
  '5.webm',
  '6.webm',
  '7.webm',
  '8.webm',
  '9.webm',
] as const

export function buildShowcaseVideos(
  baseUrl: string,
  cardIdPrefix: string,
  filenames: readonly string[] = DEFAULT_FILENAMES,
): ShowcaseVideo[] {
  return filenames.map((file, index) => {
    const n = index + 1
    const padded = n < 10 ? `0${n}` : String(n)

    return {
      id: `${cardIdPrefix}-card-${padded}`,
      previewUrl: `${baseUrl}/videos/${file}`,
    }
  })
}

// --- Sub-components ---

function Root({ children, className }: { children: React.ReactNode; className?: string }) {
  return <section className={tw('flex flex-col gap-3', className)}>{children}</section>
}

function Title({
  children,
  className,
  as: Tag = 'h2',
}: {
  children: React.ReactNode
  className?: string
  as?: 'h1' | 'h2' | 'h3'
}) {
  return <Tag className={className}>{children}</Tag>
}

function CarouselContainer({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={tw('relative w-[calc(100%+24px)] translate-x-[-12px] overflow-hidden', className)}
    >
      {children}
    </div>
  )
}

const VIDEO_ITEM_OBSERVER_OPTIONS = { threshold: 0, rootMargin: '50px' } as const

const VIDEO_ITEM_RADIUS_CLASSES = {
  default: 'rounded-studio-geometry-semantic-radius-xl',
  sm: 'rounded-studio-geometry-semantic-radius-sm',
  xs: 'rounded-studio-geometry-semantic-radius-xs',
} as const

export type ShowcaseVideoItemRadius = keyof typeof VIDEO_ITEM_RADIUS_CLASSES

function VideoItem({
  videoSrc,
  onClick,
  href,
  asChild,
  children,
  'aria-label': ariaLabel,
  className,
  radius = 'default',
}: {
  videoSrc: string
  onClick?: () => void
  href?: string
  asChild?: boolean
  children?: React.ReactNode
  'aria-label'?: string
  className?: string
  radius?: ShowcaseVideoItemRadius
}) {
  const ref = React.useRef<HTMLButtonElement & HTMLAnchorElement>(null)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const isInView = useIsInViewport(ref, false, VIDEO_ITEM_OBSERVER_OPTIONS)

  React.useEffect(() => {
    const video = videoRef.current

    if (!video) {
      return
    }

    if (isInView) {
      void video.play().catch(() => {})
      return
    }

    video.pause()
    video.currentTime = 0
  }, [isInView])

  const sharedClassName = tw(
    'group relative h-full w-full overflow-hidden border border-gray-700-opacity bg-gray-800 text-left inline-block',
    VIDEO_ITEM_RADIUS_CLASSES[radius],
    'focus-visible:ring-2 focus-visible:ring-studio-violet-500 focus-visible:outline-none',
    className,
  )

  const videoElement = (
    <video
      ref={videoRef}
      src={videoSrc}
      muted
      loop
      playsInline
      preload="auto"
      className="size-full object-cover transition-transform duration-200 group-hover:scale-105"
    />
  )

  if (asChild && children) {
    return (
      <Slot ref={ref} aria-label={ariaLabel} className={sharedClassName}>
        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement, {}, videoElement)
          : children}
      </Slot>
    )
  }

  const Component = href ? 'a' : 'button'
  const interactionProps = href ? { href } : { type: 'button' as const, onClick }

  return (
    <Component ref={ref} {...interactionProps} aria-label={ariaLabel} className={sharedClassName}>
      {videoElement}
    </Component>
  )
}

function ImageItem({
  imageSrc,
  onClick,
  href,
  'aria-label': ariaLabel,
  className,
  radius = 'default',
}: {
  imageSrc: string
  onClick?: () => void
  href?: string
  'aria-label'?: string
  className?: string
  radius?: ShowcaseVideoItemRadius
}) {
  const Component = href ? 'a' : 'button'
  const interactionProps = href ? { href } : { type: 'button' as const, onClick }

  return (
    <Component
      {...interactionProps}
      aria-label={ariaLabel}
      className={tw(
        'group relative h-full w-full overflow-hidden border border-gray-700-opacity bg-gray-800 text-left inline-block',
        VIDEO_ITEM_RADIUS_CLASSES[radius],
        'focus-visible:ring-2 focus-visible:ring-studio-violet-500 focus-visible:outline-none',
        className,
      )}
    >
      <img
        src={imageSrc}
        alt=""
        loading="lazy"
        className="size-full object-cover transition-transform duration-200 group-hover:scale-105"
      />
    </Component>
  )
}

// --- Export ---

export const ShowcaseStrip = {
  Root,
  Title,
  CarouselContainer,
  VideoItem,
  ImageItem,
}
